'use client';

import React from 'react';
import { Paginator } from '@/utils/interface';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useQueryState } from 'nuqs';

export default function ProjectPagination({ paginator }: { paginator?: Paginator }) {
    const [page, setPage] = useQueryState('page', { shallow: false });

    if (!paginator || !paginator.itemCount) return null;

    const currentPage = paginator.currentPage || parseInt(page || "1");
    const totalPages = paginator.pageCount || 1;

    const nextPage = () => {
        const next = (currentPage + 1).toString();
        setPage(next);
    };

    const prevPage = () => {
        const prev = Math.max(1, currentPage - 1).toString();
        setPage(prev);
    };

    return (
        <div className="bg-white rounded-2xl p-4 border border-blue-100/80 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs font-medium text-gray-600">
                Showing page <span className="font-bold text-gray-900">{currentPage}</span> of{' '}
                <span className="font-bold text-gray-900">{totalPages}</span> ({paginator.itemCount} projects)
            </div>

            <div className="flex items-center gap-2">
                <button
                    disabled={!paginator.hasPrevPage}
                    onClick={prevPage}
                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                    aria-label="Previous Page"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <button
                            key={p}
                            onClick={() => setPage(p.toString())}
                            className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                                currentPage === p
                                    ? 'bg-blue-600 text-white shadow-xs'
                                    : 'text-gray-600 hover:bg-slate-100'
                            }`}
                        >
                            {p}
                        </button>
                    ))}
                </div>

                <button
                    disabled={!paginator.hasNextPage}
                    onClick={nextPage}
                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                    aria-label="Next Page"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
